import React, {FC, useEffect, useState} from "react";
import GatewayService from "../../service/GatewayService";
import {DropDownCompString, getKeyVal} from "../util/DropDownComp";
import { matchesExpiryFilters } from "../../util/TimeUtil";
import TickManager from "../../service/TickManager";
import AppUtil from "../../util/AppUtil";
import {CheckboxComponent} from "../util/CheckboxComponent";
import AppConstants from "../../util/AppConstants";
import CollectionUtils from "../../util/CollectionUtils";
import ChainPositionComponent from "./ChainPositionComponent";
import {useClients} from "../../context/ClientsContext";
import {ChainPositions} from "../../models/ChainPositions";

export const PositionsComp:FC = () => {
    const [exchange, setExchange] = useState("");
    const [name, setName] = useState("");
    const [considerDay, setConsiderDay] = useState(false);
    const [positions, setPositions] = useState<Array<ChainPositions>>([]);
    const [sortField, setSortField] = useState<'margin' | 'profit' | 'moneyIn' | 'moneyOut' | 'netPosition' | 'turnOver' | 'expiry'>('expiry');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [showITM, setShowITM] = useState(true);
    const [filterNetPos, setFilterNetPos] = useState(false);
    const [showToday, setShowToday] = useState(true);
    const [showTomorrow, setShowTomorrow] = useState(true);
    const [showWeek, setShowWeek] = useState(true);
    const [showMonth, setShowMonth] = useState(true);
    const [showNextMonth, setShowNextMonth] = useState(true);
    const [showRest, setShowRest] = useState(true);
    const [showAll, setShowAll] = useState(true);
    const [showCE, setShowCE] = useState(true);
    const [showPE, setShowPE] = useState(true);
    const [showOTM, setShowOTM] = useState(true);
    const [showLong, setShowLong] = useState(true);
    const [showShort, setShowShort] = useState(true);
    const [analysedGroup, setAnalysedGroup] = useState<number|null>(null);
    const [expandedGroups, setExpandedGroups] = useState<{[idx: number]: boolean}>({});
    const [allExpanded, setAllExpanded] = useState(true);
    const { clients } = useClients();
    const [client, setClient] = useState(clients[0]);

    const sortOptions = [
        { value: 'expiry', label: 'Expiry' },
        { value: 'margin', label: 'Margin' },
        { value: 'profit', label: 'Profit' },
        { value: 'moneyIn', label: 'Money In' },
        { value: 'moneyOut', label: 'Money Out' },
        { value: 'netPosition', label: 'Net Position' },
        { value: 'turnOver', label: 'Turnover' },
    ];

    useEffect(()=>{
        refresh()
    }, [client])

    useEffect(()=>{
        setShowToday(showAll)
        setShowTomorrow(showAll)
        setShowWeek(showAll)
        setShowNextMonth(showAll)
        setShowMonth(showAll)
        setShowRest(showAll)
    }, [showAll])

    useEffect(() => {
        // By default, collapse all groups
        const initial: {[idx: number]: boolean} = {};
        sortedPositions.forEach((_, idx) => { initial[idx] = false; });
        setExpandedGroups(initial);
        setAllExpanded(false);
    }, []);

    const toggleGroup = (idx: number) => {
        setExpandedGroups(exp => ({ ...exp, [idx]: !exp[idx] }));
    };
    const setAllGroups = (expand: boolean) => {
        const newState: {[idx: number]: boolean} = {};
        sortedPositions.forEach((_, idx) => { newState[idx] = expand; });
        setExpandedGroups(newState);
        setAllExpanded(expand);
    };

    async function refresh() {
        if (client === '') {
            // Fetch and merge positions for all clients
            const allPositions = await Promise.all(
                clients.filter(c => c !== '').map(async c => (await GatewayService.getPositions(c, considerDay)))
            );
            setPositions(allPositions.flat());
        } else {
            setPositions((await GatewayService.getPositions(client, considerDay)));
        }
    }


    function getPos() {
        return positions.filter(it=>{
            return name=="" || TickManager.getOption(it.positions[0].tradingsymbol).genInfo.name==name
        }).sort((a,b)=>{
            const e1 = a.minExpiry
            const e2 = b.minExpiry
            return e1 - e2
        }).filter(it=>{
            let ex = TickManager.getOption(it.positions[0].tradingsymbol).genInfo.exchange
            return exchange=="" || ex==exchange
        } )
    }
    let filteredPositions = getPos()
        .filter(mp => !filterNetPos || mp.netPosition !== 0)
        .filter(mp => matchesExpiryFilters({showToday, showTomorrow, showWeek, showMonth, showNextMonth, showRest, showAll}, mp.minExpiry))
        .map(mp => ({
            ...mp,
            positions: mp.positions
                // Long/Short filtering at row level using quantity sign
                .filter(row => {
                    if (showLong && showShort) return true;
                    if (showLong) return row.quantity > 0;
                    if (showShort) return row.quantity < 0;
                    return false;
                })
                // CE/PE instrument type filters
                .filter(row => {
                    const inst = TickManager.getOption(row.tradingsymbol).genInfo.instrument_type;
                    if (inst === AppConstants.CE) return showCE;
                    if (inst === AppConstants.PE) return showPE;
                    return true; // futures/others unaffected
                })
                // OTM filter: exclude OTM when toggle is off (futures are never OTM)
                .filter(row => {
                    const inst = TickManager.getOption(row.tradingsymbol).genInfo.instrument_type;
                    const isFuture = inst !== AppConstants.CE && inst !== AppConstants.PE;
                    const isOTM = !isFuture && row.intrinsic === 0;
                    const isITM = !isFuture && row.intrinsic > 0;
                    if (isFuture)return true
                    if (!showITM && isITM) return false
                    if (!showOTM && isOTM)return false
                    return true
                })
        }))
        .filter(mp => mp.positions.length > 0);
    let sortedPositions = [...filteredPositions].sort((a, b) => {
        let aVal: number, bVal: number;
        if (sortField === 'expiry') {
            aVal = a.minExpiry;
            bVal = b.minExpiry;
        } else {
            aVal = a[sortField] ?? 0;
            bVal = b[sortField] ?? 0;
        }
        if (sortOrder === 'asc') return aVal - bVal;
        return bVal - aVal;
    });

    let groupedTables = sortedPositions.map((mp, idx) => (
        <ChainPositionComponent
            key={idx}
            mp={mp}
            idx={idx}
            expanded={!!expandedGroups[idx] || allExpanded}
            analysedGroup={analysedGroup}
            toggleGroup={toggleGroup}
            setAnalysedGroup={setAnalysedGroup}
        />
    ));
    return<>
        <div className="positions-header">
            <div className="positions-controls">
                <div className="control-group">
                    <h3>Actions ({sortedPositions.flatMap(it => it.positions).length})</h3>
                    <button className="expand-btn" onClick={() => setAllGroups(!allExpanded)}>
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                    <button onClick={() => AppUtil.exportChainPositionsCSV(filteredPositions)}>Export CSV</button>
                    <button onClick={()=>refresh()}>Refresh</button>
                </div>
                
                <div className="control-group">
                    <h3>Sort & Filter</h3>
                    <div className="sort-controls">
                        <select className="positions-sort-dropdown" value={sortField} onChange={e => setSortField(e.target.value as any)}>
                            {sortOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <button onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>

                <div className="control-group">
                    <h3>View Options</h3>

                    <CheckboxComponent text={"NetPos ≠ 0"} actual={filterNetPos} val={filterNetPos} set={setFilterNetPos}/>
                    <CheckboxComponent text={"CONSIDER DAY ONLY"} actual={considerDay} val={considerDay} set={setConsiderDay}/>
                </div>

                <div className="control-group">
                    <h3>Instrument Types</h3>
                    <CheckboxComponent text={"ITM"} actual={showITM} val={showITM} set={setShowITM}/>
                    <CheckboxComponent text={"OTM"} actual={showOTM} val={showOTM} set={setShowOTM}/>
                    <CheckboxComponent text={"CE"} actual={showCE} val={showCE} set={setShowCE}/>
                    <CheckboxComponent text={"PE"} actual={showPE} val={showPE} set={setShowPE}/>
                </div>
                
                <div className="control-group">
                    <h3>Position Types</h3>
                    <CheckboxComponent text={"Long (>0)"} actual={showLong} val={showLong} set={setShowLong}/>
                    <CheckboxComponent text={"Short (<0)"} actual={showShort} val={showShort} set={setShowShort}/>
                </div>
                
                <div className="control-group">
                    <h3>Data Source</h3>
                    <DropDownCompString text={"Client"} actual={client} arr={getKeyVal(clients)} set={setClient}/>
                    <DropDownCompString text={"Exchange"} actual={exchange} arr={getKeyVal(["MCX", "NFO"])} set={setExchange}/>
                    <DropDownCompString text={"Symbol"} actual={name} arr={getKeyVal(positions.map(it=>TickManager.getOption(it.positions[0].tradingsymbol).genInfo.name))} set={setName}/>
                </div>
            </div>
            
            <div className="expiry-filters">
                <h3>Expiry Filters({filteredPositions.flatMap(it => it.positions).length})</h3>
                <div className="expiry-checkboxes">
                    <CheckboxComponent text={"All"} actual={showAll} val={showAll} set={setShowAll}/>
                    <CheckboxComponent text={"Today"} actual={showToday} val={showToday} set={setShowToday}/>
                    <CheckboxComponent text={"Tomorrow"} actual={showTomorrow} val={showTomorrow} set={setShowTomorrow}/>
                    <CheckboxComponent text={"This Week"} actual={showWeek} val={showWeek} set={setShowWeek}/>
                    <CheckboxComponent text={"This Month"} actual={showMonth} val={showMonth} set={setShowMonth}/>
                    <CheckboxComponent text={"Next Month"} actual={showNextMonth} val={showNextMonth} set={setShowNextMonth}/>
                    <CheckboxComponent text={"Rest"} actual={showRest} val={showRest} set={setShowRest}/>
                </div>
            </div>
        </div>
        <div className="positions-summary-row">
            <div className="positions-summary-card">expected turnover = {AppUtil.getRoundedOff(CollectionUtils.sumOf(filteredPositions, "turnOver")/100000)}L</div>
            <div className="positions-summary-card">expected profit = {AppUtil.getRoundedOff(CollectionUtils.sumOf(filteredPositions, "profit")/100000)}L</div>
            <div className="positions-summary-card">money in = {AppUtil.getRoundedOff(CollectionUtils.sumOf(filteredPositions, "moneyIn")/100000)}L</div>
            <div className="positions-summary-card">money out = {AppUtil.getRoundedOff(CollectionUtils.sumOf(filteredPositions, "moneyOut")/100000)}L</div>
            <div className="positions-summary-card">margin = {AppUtil.getRoundedOff(CollectionUtils.sumOf(filteredPositions, "margin")/100000)}L</div>
        </div>
        {groupedTables}
    </>
}
